import React, { useEffect, useRef, useState } from 'react';

// The fixed design surface. Templates are authored at 1920x1080 and scaled to fit.
export const CANVAS = { w: 1920, h: 1080 };

export const WIDGET_TYPES = [
  { type: 'text', label: 'Text' },
  { type: 'image', label: 'Image' },
  { type: 'clock', label: 'Clock' },
  { type: 'list', label: 'List' },
  { type: 'shape', label: 'Shape' },
  { type: 'datafeed', label: 'Data feed' },
  { type: 'html', label: 'HTML' },
];

let _seq = 0;
const uid = () => `w${Date.now().toString(36)}${(_seq++).toString(36)}`;

// Factory for a new widget with sensible defaults.
export function newWidget(type) {
  const base = { id: uid(), type, x: 760, y: 440, w: 400, h: 200, props: {} };
  switch (type) {
    case 'text':
      return { ...base, props: { text: 'New text', fontSize: 48, color: '#ffffff', fontWeight: '600', textAlign: 'left' } };
    case 'image':
      return { ...base, w: 480, h: 320, props: { src: '', fit: 'cover' } };
    case 'clock':
      return { ...base, w: 520, h: 240, props: { format: '24h', showDate: true, color: '#ffffff', fontSize: 96 } };
    case 'list':
      return { ...base, w: 560, h: 480, props: { title: 'List', items: ['Item one', 'Item two', 'Item three'], color: '#ffffff', fontSize: 36 } };
    case 'datafeed':
      // Wide enough that the default columns are readable, so centre it —
      // at the shared default x it would spawn half off the canvas.
      return { ...base, x: 360, y: 240, w: 1200, h: 600, props: {
        sourceId: '', title: 'Data Feed', color: '#ffffff', fontSize: 32,
        layout: 'table', maxRows: 10, headerBg: 'rgba(255,255,255,0.1)', rowAltBg: 'rgba(255,255,255,0.03)',
        columns: [{ key: 'col1', header: 'Column 1', flex: '1' }, { key: 'col2', header: 'Column 2', flex: '1' }],
        sampleData: [{ col1: 'Row 1', col2: 'Value 1' }, { col1: 'Row 2', col2: 'Value 2' }]
      }};
    case 'shape':
      return { ...base, props: { background: '#1f6feb', borderRadius: 16 } };
    case 'html':
      return { ...base, w: 600, h: 400, props: { html: '<div style="color:#fff;font-size:40px;padding:24px">Custom HTML</div>' } };
    default:
      return base;
  }
}

export function emptyTemplate() {
  return { version: 1, background: '#0b1020', widgets: [] };
}

// Renders the inner content of a single widget, filling its parent box (100% x 100%).
// Shared by the live editor and the read-only preview so they always look identical.
export function WidgetContent({ widget }) {
  const p = widget.props || {};
  const box = {
    width: '100%', height: '100%', overflow: 'hidden',
    borderRadius: p.borderRadius ? `${p.borderRadius}px` : undefined,
    border: p.border || undefined,
    background: p.background || undefined,
    boxSizing: 'border-box',
  };

  switch (widget.type) {
    case 'text':
      return (
        <div style={{
          ...box, color: p.color || '#fff', fontSize: p.fontSize || 48,
          fontWeight: p.fontWeight || '400', textAlign: p.textAlign || 'left',
          padding: p.padding != null ? p.padding : 8,
          display: 'flex', alignItems: 'center',
          justifyContent: p.textAlign === 'center' ? 'center' : p.textAlign === 'right' ? 'flex-end' : 'flex-start',
          whiteSpace: 'pre-wrap', lineHeight: 1.2,
        }}>
          <span style={{ width: '100%' }}>{p.text || ''}</span>
        </div>
      );
    case 'image':
      return p.src
        ? <img alt="" src={p.src} style={{ ...box, objectFit: p.fit || 'cover' }} />
        : <div style={{ ...box, background: '#1b2333', color: '#5b6b86', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>No image</div>;
    case 'clock':
      return <ClockWidget p={p} box={box} />;
    case 'list':
      return (
        <div style={{ ...box, color: p.color || '#fff', fontSize: p.fontSize || 36, padding: p.padding != null ? p.padding : 12 }}>
          {p.title && <div style={{ fontWeight: 700, marginBottom: 12 }}>{p.title}</div>}
          {(p.items || []).map((it, i) => (
            <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.12)', display: typeof it === 'object' ? 'flex' : 'block', justifyContent: 'space-between' }}>
              {typeof it === 'object' ? (<><span>{it.label}</span><span style={{ opacity: 0.85 }}>{it.value}</span></>) : it}
            </div>
          ))}
        </div>
      );
    case 'datafeed': {
      const cols = p.columns || [];
      const sampleData = p._liveData || p.sampleData || [];
      const rows = Array.isArray(sampleData) ? sampleData.slice(0, p.maxRows || 20) : [];
      const headerBg = p.headerBg || 'rgba(255,255,255,0.1)';
      const rowAltBg = p.rowAltBg || 'rgba(255,255,255,0.03)';
      return (
        <div style={{ ...box, color: p.color || '#fff', fontSize: p.fontSize || 32, overflow: 'hidden' }}>
          {p.title && <div style={{ fontWeight: 700, marginBottom: 12, fontSize: (p.fontSize || 32) * 1.2 }}>{p.title}</div>}
          {cols.length > 0 && (
            <div style={{ display: 'flex', padding: '10px 12px', background: headerBg, fontWeight: 700, borderRadius: '6px 6px 0 0', gap: 8 }}>
              {cols.map((c, i) => <div key={i} style={{ flex: c.flex || 1, textAlign: c.align || 'left' }}>{c.header || c.key}</div>)}
            </div>
          )}
          {rows.map((row, i) => (
            <div key={i} style={{ display: 'flex', padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)', gap: 8, background: i % 2 === 1 ? rowAltBg : undefined }}>
              {cols.length > 0
                ? cols.map((c, j) => <div key={j} style={{ flex: c.flex || 1, textAlign: c.align || 'left', color: c.color, fontWeight: c.fontWeight }}>{(c.prefix || '') + (row[c.key] ?? '') + (c.suffix || '')}</div>)
                : typeof row === 'object'
                  ? <>{Object.values(row).map((v, j) => <span key={j} style={{ flex: 1 }}>{String(v)}</span>)}</>
                  : <span>{String(row)}</span>
              }
            </div>
          ))}
          {rows.length === 0 && <div style={{ opacity: 0.4, padding: 16 }}>Waiting for data…</div>}
        </div>
      );
    }
    case 'html':
      // Sandboxed in an iframe with NO capabilities: this preview renders shared
      // templates authored by other users, so their HTML must never run scripts
      // or reach the dashboard's origin/session. Renders static HTML/CSS only.
      return (
        <iframe
          title="html-widget"
          sandbox=""
          srcDoc={p.html || ''}
          scrolling="no"
          style={{ ...box, border: p.border || 'none' }}
        />
      );
    case 'shape':
    default:
      return <div style={box} />;
  }
}

function ClockWidget({ p, box }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ ...box, color: p.color || '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontSize: p.fontSize || 96, fontWeight: p.fontWeight || 600 }}>
        {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: (p.format || '24h') === '12h' })}
      </div>
      {p.showDate !== false && (
        <div style={{ fontSize: (p.fontSize || 96) * 0.35, opacity: 0.8 }}>
          {now.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
        </div>
      )}
    </div>
  );
}

// Measures an element's rendered width (via ResizeObserver) so previews and
// the editor canvas can scale to whatever space the layout gives them.
export function useElementWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => setWidth(entries[0].contentRect.width));
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

// Read-only, scaled preview of a whole template. `width` is the rendered pixel width.
export default function TemplateRenderer({ template, width = 480 }) {
  const t = template || emptyTemplate();
  const scale = width / CANVAS.w;
  return (
    <div style={{ width, height: CANVAS.h * scale, overflow: 'hidden', background: '#000' }}>
      <div style={{
        width: CANVAS.w, height: CANVAS.h, transform: `scale(${scale})`, transformOrigin: 'top left',
        position: 'relative', background: t.background || '#000',
      }}>
        {(t.widgets || []).map((w, i) => (
          <div key={w.id || i} style={{ position: 'absolute', left: w.x, top: w.y, width: w.w, height: w.h }}>
            <WidgetContent widget={w} />
          </div>
        ))}
      </div>
    </div>
  );
}

// A TemplateRenderer that fills the width of its container.
export function AutoPreview({ template }) {
  const [ref, width] = useElementWidth();
  return (
    <div ref={ref} style={{ width: '100%' }}>
      {width > 0 && <TemplateRenderer template={template} width={width} />}
    </div>
  );
}
